import _ from 'lodash';
import { SIDEBAR_NAV_ITEMS } from '../../constants/sidebarNavConfig';

/**
 * Map: permission flag → URL prefixes ของเมนูที่อยู่ใน module นั้น
 * ใช้สำหรับ derive permissions จาก system_role.allowed_menus
 */
const PERM_PREFIX_MAP = {
  sale:             ['/sale/'],
  finance_approve:  ['/finance/'],
  hr_management:    ['/humen/'],
  hr_report:        ['/humen/'],
  hr_edit:          ['/humen/'],
  director:         ['/director/'],
  customer:         ['/customer/'],
  project:          ['/project/'],
  marketing:        ['/marketing/'],
};

/**
 * allowed_menus เก็บได้ 2 แบบ: href ตรงๆ ('/finance/expenses') หรือ module key
 * เต็มโมดูล ('FINANCE', มาจากตอนติ๊ก "ทั้งโมดูล" ใน RoleList) — แปลง module key
 * ให้กลายเป็น prefix เทียบเท่า (`/finance/`) ก่อน เพื่อให้ logic startsWith เดิม
 * ใช้ได้ทั้งสองแบบโดยไม่ต้องแก้จุดอื่น
 */
const MODULE_PREFIX = Object.fromEntries(
  SIDEBAR_NAV_ITEMS.map((item) => [item.name, `/${item.href.split('/')[1]}/`]),
);

const expandModuleKeys = (allMenus) => allMenus.map((h) => MODULE_PREFIX[h] || h);

/**
 * คำนวณ permissions จาก system_role.allowed_menus
 * - Role ที่ allowed_menus ว่าง (ไม่จำกัด) → ได้ทุก permission รวม admin
 * - Role ที่มีเมนูใน module X → ได้ permission ของ module X
 * ผลลัพธ์ควร OR กับ DB permissions ไม่ใช่แทนที่
 */
export const derivePermissionsFromRoles = (roles) => {
  const arr = Array.isArray(roles) ? roles : (roles ? [roles] : []);
  if (arr.length === 0) return {};

  // unrestricted role → all permissions
  if (arr.some((r) => !r?.allowed_menus?.length)) {
    return Object.fromEntries(
      [...Object.keys(PERM_PREFIX_MAP), 'admin'].map((k) => [k, true]),
    );
  }

  const allMenus = expandModuleKeys(arr.flatMap((r) => r?.allowed_menus || []));
  const derived = {};
  Object.entries(PERM_PREFIX_MAP).forEach(([perm, prefixes]) => {
    derived[perm] = prefixes.some((prefix) => allMenus.some((h) => h.startsWith(prefix)));
  });
  return derived;
};

const accessRight = (me, moduleName, levelPage = 0) => {
  const lvl = levelPage ?? 0;
  const {
    userData: {
      department: { access = {} } = {},
      role: { level: meLevel } = {},
    } = {},
  } = me || {};

  const userLevel = meLevel != null ? meLevel : 0;
  if (access[`${_.toUpper(moduleName)}`] && userLevel >= lvl) {
    return true;
  }
  return false;
};

/**
 * ถ้ามีการตั้งค่า subMenuAccess[MODULE] อย่างน้อย 1 key จะใช้โหมด whitelist (เฉพาะ href ที่เป็น true)
 * ถ้าไม่มีการตั้งค่าเลยต่อโมดูล = ไม่จำกัดเมนูย่อย (ถ้าเข้าโมดูลได้)
 */
export const subMenuAllowed = (access, moduleName, href) => {
  if (!access || !moduleName || !href) return true;
  const mod = _.toUpper(moduleName);
  const sub = access.subMenuAccess && access.subMenuAccess[mod];
  if (!sub || typeof sub !== 'object' || Object.keys(sub).length === 0) {
    return true;
  }
  return sub[href] !== false;
};

const PROFILE_MENUS = [
  '/profile/timestamp',
  '/profile/ot',
  '/profile/disbursement',
  '/profile/leave',
  '/profile/field-allowance',
  '/profile/profile',
];

export const accessRightSubMenu = (me, moduleName, href) => {
  const rawRoles = me?.userData?.system_role;
  const roles = Array.isArray(rawRoles) ? rawRoles : (rawRoles ? [rawRoles] : []);

  // ไม่มี role → เห็นเฉพาะ menu profile
  if (roles.length === 0) return PROFILE_MENUS.includes(href);

  // มี role ใดที่ allowed_menus ว่าง (ไม่จำกัด) → เห็นทุกเมนู
  if (roles.some((r) => !r?.allowed_menus?.length)) return true;

  // รวม allowed_menus จากทุก role — อาจเป็น href ตรงๆ หรือ module key เต็มโมดูล
  // (เช่น 'ACCOUNTING' ตอนติ๊กทั้งโมดูล) ถ้ามี module key ของ href นี้อยู่ ถือว่าเข้าถึงได้
  // แม้ href นี้จะเพิ่งถูกเพิ่มเข้ามาใหม่ทีหลังก็ตาม (ไม่ต้องกลับไปติ๊กเมนูใหม่ทีละอัน)
  const combined = new Set(roles.flatMap((r) => r?.allowed_menus || []));
  return combined.has(href) || (!!moduleName && combined.has(_.toUpper(moduleName)));
};

// permission flag เก่า (Employee.permissions ใน backend/models/Employee.js — ระบบสิทธิ์รุ่นก่อน
// Access model/system_role ปัจจุบัน) ที่ยังมีความหมายอยู่กับบางโมดูล — Sale.js เดิมเช็ค
// perms.admin/perms.sale เองตรงๆ อยู่แล้ว (ไม่ได้ผ่าน accessRight.js เลย) ต้อง OR รวมเข้ามาด้วย
// ไม่งั้น route guard ตัวใหม่จะเข้มกว่าที่ระบบเดิมเคยอนุญาตไว้ (บล็อกคนที่เคยเข้าได้)
const LEGACY_PERMISSION_KEYS = {
  SALE: ['sale'],
  FINANCE: ['finance_approve'],
  HUMEN: ['hr_management', 'hr_report', 'hr_edit'],
};

// เช็คว่า user มีสิทธิ์เข้า "โมดูล" นี้เลยไหม (หยาบกว่า accessRightSubMenu ที่เช็คเป็นรายเมนูย่อย) —
// ใช้เป็น route guard ที่ระดับ layout กัน user พิมพ์ URL เดามั่วๆ เข้าโมดูลที่ไม่มีสิทธิ์ตรงๆ (เดิมไม่มี
// การเช็คนี้เลยที่ระดับ layout — sidebar แค่ซ่อนเมนูไม่ให้เห็น แต่พิมพ์ URL เองยังเข้าถึงหน้าได้ปกติ)
// OR รวม 4 แหล่งที่ระบบนี้มี (ตั้งใจให้กว้าง/ปลอดภัยไว้ก่อน — พลาดแล้วบล็อกคนที่ควรเข้าได้อยู่แย่กว่า
// พลาดแล้วปล่อยผ่านคนที่ backend ก็ยัง enforce สิทธิ์จริงอยู่ดีในชั้น API):
// 1) admin flag เก่า (escape hatch), 2) legacy permission key เฉพาะโมดูล (ดู Sale.js เดิม),
// 3) department.access[MODULE] (accessRight ปกติ), 4) system_role.allowed_menus (เหมือนที่
// MainSidebar.js ใช้ตัดสินใจโชว์ไอคอนโมดูลในแถบเมนู — มีเมนูย่อยอย่างน้อย 1 อันที่เข้าถึงได้ =
// ถือว่ามีสิทธิ์เข้าโมดูล) — โมดูลที่ไม่ได้อยู่ใน SIDEBAR_NAV_ITEMS เลย (เช่น MONITOR ที่เป็นหน้า
// internal ไม่ผูก sidebar) ถือว่าผ่านเสมอ (safe default, ไม่บล็อกสิ่งที่ระบบเดิมไม่เคยจำกัด)
export const hasModuleAccess = (me, moduleName) => {
  const perms = me?.userData?.permissions || {};
  if (perms.admin) return true;
  if ((LEGACY_PERMISSION_KEYS[moduleName] || []).some((k) => perms[k])) return true;
  if (accessRight(me, moduleName)) return true;

  const items = SIDEBAR_NAV_ITEMS.filter((item) => item.name === moduleName);
  if (items.length === 0) return true;
  return items.some((item) => (item.sub && item.sub.length > 0
    ? item.sub.some((s) => accessRightSubMenu(me, item.name, s.href, s.level ?? 0))
    : accessRightSubMenu(me, item.name, item.href, 0)));
};

export default accessRight;

/*
การใช้งาน

me คือข้อมูล user ผู้ใช้งานระบบที่ได้จาก server

module คือชื่อ module จาก layout เช่น TMS CRM เป็นต้น

levelPage สำหรับกำหนดระดับการเข้าถึงของ Page นั้น ๆ
ทั้งนี้หากต้องการตรวจสอบการเข้าถึงโมดูลไม่จำเป็นต้องส่งข้อมูลมาแต่ใช้ค่าเริ่มต้นในฟังก์ชันแทน

accessRightSubMenu — ตรวจทั้งโมดูล ระดับบทบาท และสิทธิ์เมนูย่อย (href ตรงกับ Sidebar)
*/
